import { useEffect, useState, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase';
import {
  ApplianceCategory,
  RecallStatus,
} from '@/types';
import ApplianceCard from '@/components/appliances/ApplianceCard';
import { useNavigate } from 'react-router-dom';

// Helper types
type RiskSortKey = 'high' | 'medium' | 'low' | 'null';

interface Appliance {
  id: string;
  name: string;
  make: string;
  model: string;
  modelNumber?: string;
  purchaseDate?: string;
  warrantyExpiresAt?: string;
  manualUrl?: string;
  photoUrl?: string;
  category?: ApplianceCategory;
  recallStatus?: RecallStatus;
  totalServiceCost?: number;
  purchasePrice?: number;
  householdId: string;
  addedBy: string;
  createdAt: string;
}

const RISK_SORT_ORDER: Record<RiskSortKey, number> = {
  high: 0,
  medium: 1,
  low: 2,
  null: 3,
};

const getCategoryIcon = (category?: ApplianceCategory) => {
  switch (category) {
    case 'kitchen': return (
      <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
    case 'laundry': return (
      <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    );
    case 'hvac': return (
      <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
    case 'plumbing': return (
      <svg className="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    );
    case 'electrical': return (
      <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
    case 'other': return (
      <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
    default: return (
      <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
};

const getRecallStatusDot = (status?: RecallStatus) => {
  switch (status) {
    case 'recalled': return (
      <div className="h-2.5 w-2.5 rounded-full bg-red-500" title="Recall detected" />
    );
    case 'unknown': return (
      <div className="h-2.5 w-2.5 rounded-full bg-yellow-500" title="Unknown recall status" />
    );
    case 'clear': return (
      <div className="h-2.5 w-2.5 rounded-full bg-green-500" title="No recalls detected" />
    );
    default: return null;
  }
};

export function ApplianceWidget({ householdId }: { householdId: string | null }) {
  const [appliances, setAppliances] = useState<Appliance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // For recalls count
  const [recallCounts, setRecallCounts] = useState<Record<string, number>>({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!householdId) return;

    // Subscribe to appliances
    const appliancesRef = collection(db, `households/${householdId}/appliances`);
    const appliancesQuery = query(
      appliancesRef,
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    
    const unsubscribe = onSnapshot(
      appliancesQuery,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Appliance[];
        
        setAppliances(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching appliances:', err);
        setError('Failed to load appliances');
        setLoading(false);
      }
    );
    
    // Also fetch recall counts for all appliances
    const fetchRecallCounts = async () => {
      try {
        const counts: Record<string, number> = {};
        const appliancesSnapshot = await getDocs(appliancesQuery);
        
        for (const doc of appliancesSnapshot.docs) {
          const recallRef = collection(db, `households/${householdId}/appliances/${doc.id}/recalls`);
          const recallsSnapshot = await getDocs(recallRef);
          counts[doc.id] = recallsSnapshot.size;
        }
        
        setRecallCounts(counts);
      } catch (err) {
        console.error('Error fetching recall counts:', err);
      }
    };
    
    fetchRecallCounts();
    
    return () => unsubscribe();
  }, [householdId]);

  // Sort appliances by risk score
  const sortedAppliances = useMemo(() => {
    return [...appliances].sort((a, b) => {
      const aRisk: RiskSortKey = (a.recallStatus as RiskSortKey) || 'null';
      const bRisk: RiskSortKey = (b.recallStatus as RiskSortKey) || 'null';

      const aScore = RISK_SORT_ORDER[aRisk] ?? RISK_SORT_ORDER['null'];
      const bScore = RISK_SORT_ORDER[bRisk] ?? RISK_SORT_ORDER['null'];
      
      if (aScore !== bScore) {
        return aScore - bScore;
      }
      
      // Secondary sort: most recently added
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [appliances]);

  // Total recall count
  const totalRecalls = useMemo(() => {
    return Object.values(recallCounts).reduce((sum, count) => sum + count, 0);
  }, [recallCounts]);

  // Handle navigation
  const handleNavigateToAppliances = () => {
    navigate('/appliances');
  };

  if (!householdId) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg p-6 text-center">
        <p className="text-slate-600 dark:text-slate-300">
          Link a household to track appliances
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          Appliance Status
        </h2>
        {totalRecalls > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            {totalRecalls} {totalRecalls === 1 ? 'Recall' : 'Recalls'}
          </span>
        )}
      </div>

      {/* Appliances list */}
      {sortedAppliances.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-slate-500 dark:text-slate-400">
            No appliances added
          </p>
          <button
            onClick={handleNavigateToAppliances}
            className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
          >
            Add appliances now
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedAppliances.slice(0, 3).map((appliance) => (
            <div 
              key={appliance.id}
              onClick={() => navigate('/appliances')}
              className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-3">
                {getCategoryIcon(appliance.category)}
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {appliance.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {appliance.make} {appliance.model}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getRecallStatusDot(appliance.recallStatus)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
        <button
          onClick={handleNavigateToAppliances}
          className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
        >
          Manage Appliances →
        </button>
      </div>
    </div>
  );
}
